import { OneWayDragger } from "./OneWayDragger";
import { trackShape } from "@/analytics/drawing-analytics";
import { TwoPointDragger } from "./TwoPointDragger";
import { DualModeDragger } from "./DualModeDragger";
import {
  DraggerType,
  type Point2D,
  type Point3D,
  type DraggerConfig,
} from "./types";
import type { DrawingModule } from "../types/DrawingWeb";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle, getCurrentSpaceId } from "../core/oda";
import { OpenMode } from "../core/constants";
import { AddEntitiesCommand } from "../commands/AddEntitiesCommand";
import { PREVIEW_SHAPE_STYLE } from "./previewStyles";
import { MeasurementLabels } from "../overlay/MeasurementLabels";
import { getBoundsFromPoints, type PointBounds } from "./pointGeometry";

/**
 * Build an OdDbEllipse inscribed in the bounding box from corner to corner.
 * Pure/shared: used by both the classic single-stroke engine and the
 * two-point sequential engine below.
 */
function buildEllipseEntity(
  viewer: DRDrawingViewer,
  min: Point3D,
  max: Point3D,
): { id: DrawingModule.OdDbObjectId } | null {
  const module = viewer.module;
  const db = viewer.database;

  if (!db) {
    return null;
  }

  const centerX = (min.x + max.x) / 2;
  const centerY = (min.y + max.y) / 2;
  const radiusX = Math.abs(max.x - min.x) / 2;
  const radiusY = Math.abs(max.y - min.y) / 2;

  const ellipse = module.OdDbEllipse.createObject();
  ellipse.setDatabaseDefaults(db, true);

  const center = new module.OdGePoint3d(centerX, centerY, 0);
  const normal = new module.OdGeVector3d(0, 0, 1); // Z-up

  const majorRadius = Math.max(radiusX, radiusY);
  const minorRadius = Math.min(radiusX, radiusY);
  const radiusRatio = minorRadius / majorRadius;

  let majorAxis: DrawingModule.OdGeVector3d;
  if (radiusX >= radiusY) {
    majorAxis = new module.OdGeVector3d(majorRadius, 0, 0);
  } else {
    majorAxis = new module.OdGeVector3d(0, majorRadius, 0);
  }

  ellipse.set(center, normal, majorAxis, radiusRatio, 0, Math.PI * 2);

  deleteObj(center);
  deleteObj(normal);
  deleteObj(majorAxis);

  const currentSpaceId = getCurrentSpaceId(db);
  const currentSpace = openAs(
    currentSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForWrite,
  );
  const entityId = currentSpace.appendOdDbEntity(ellipse);

  deleteObj(currentSpace);
  deleteObj(ellipse);

  return { id: entityId };
}

/**
 * Classic single-stroke ellipse: press = corner A, release = corner B.
 * Body unchanged from the original EllipseDragger (aside from a pre-existing
 * `screenY` typo in createPreview() fixed to `screen.y`) — only the raw
 * entity geometry (buildEllipseEntity) was pulled out so the two-point
 * engine below can reuse it.
 */
export class EllipseOneWayEngine extends OneWayDragger {
  readonly type = DraggerType.Ellipse;

  private ellipsePreviewId: string | null = null;
  private measureLabels: MeasurementLabels | null = null;

  // ============== Preview ==============

  protected createPreview(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    this.ellipsePreviewId = overlay.createEllipse(
      screen.x,
      screen.y,
      0,
      0,
      0,
      PREVIEW_SHAPE_STYLE,
    );
    if (this.ellipsePreviewId) {
      this.addPreviewId(this.ellipsePreviewId);
    }
  }

  protected updatePreview(): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay || !this.ellipsePreviewId) return;

    const bounds = this.getBounds();
    if (!bounds) return;

    const cx = bounds.screenMin.x + bounds.width / 2;
    const cy = bounds.screenMin.y + bounds.height / 2;
    const radiusx = bounds.width / 2;
    const radiusy = bounds.height / 2;

    overlay.update(this.ellipsePreviewId, {
      cx,
      cy,
      radiusx,
      radiusy,
    });

    this.updateMeasurements(bounds);
  }

  private updateMeasurements(bounds: PointBounds): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    if (!this.measureLabels) {
      this.measureLabels = new MeasurementLabels(
        overlay,
        this.viewer.getBackgroundColor(),
      );
    }
    this.measureLabels.setLine(
      "width",
      bounds.screenMin.x,
      bounds.screenMin.y,
      bounds.screenMax.x,
      bounds.screenMin.y,
    );
    this.measureLabels.setLabel(
      "width",
      bounds.screenMin.x + bounds.width / 2,
      bounds.screenMin.y - 18,
      MeasurementLabels.formatDistance(bounds.worldWidth),
    );
    this.measureLabels.setLine(
      "height",
      bounds.screenMax.x,
      bounds.screenMin.y,
      bounds.screenMax.x,
      bounds.screenMax.y,
    );
    this.measureLabels.setLabel(
      "height",
      bounds.screenMax.x + 18,
      bounds.screenMin.y + bounds.height / 2,
      MeasurementLabels.formatDistance(bounds.worldHeight),
    );
  }

  // ============== Entity Creation ==============

  protected createEntity(): void {
    const bounds = this.getBounds();
    if (!bounds) return;

    const db = this.viewer.database;
    if (!db) return;

    const noteConfig = this.viewer.activeNoteConfig;
    const entityInfo = buildEllipseEntity(
      this.viewer,
      bounds.worldMin,
      bounds.worldMax,
    );

    if (entityInfo) {
      this.applyNoteConfigToEntity(entityInfo.id);

      const entityIds: DrawingModule.OdDbObjectId[] = [entityInfo.id];

      const hatchId = this.createHatchForEntity(entityInfo.id);
      if (hatchId) {
        entityIds.push(hatchId);
      }

      const centerX = (bounds.worldMin.x + bounds.worldMax.x) / 2;
      const centerY = (bounds.worldMin.y + bounds.worldMax.y) / 2;
      const labelId = this.createLabelIfEnabled(centerX, centerY);
      if (labelId) entityIds.push(labelId);

      const command = new AddEntitiesCommand(this.viewer, {
        entityHandles: entityIds.map((id) => getEntityHandle(id)),
        originalLayerName: noteConfig.props.layername ?? "0",
      });
      this.viewer.executeCommand(command);
      trackShape("ellipse");

      const extras = labelId ? [labelId] : [];
      this.selectCreatedEntity(entityInfo.id, extras);
      for (const id of entityIds) {
        deleteObj(id);
      }
    }
  }

  protected clear(): void {
    super.clear();
    this.measureLabels?.clear();
    this.measureLabels = null;
    this.ellipsePreviewId = null;
  }
}

/**
 * Two-point ellipse: press-drag-release places corner A, press-drag-release
 * again places corner B and creates the entity.
 */
export class EllipseTwoPointEngine extends TwoPointDragger {
  readonly type = DraggerType.Ellipse;

  private ellipsePreviewId: string | null = null;
  private measureLabels: MeasurementLabels | null = null;

  // ============== Preview ==============

  protected createPreview(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    this.ellipsePreviewId = overlay.createEllipse(
      screen.x,
      screen.y,
      0,
      0,
      0,
      PREVIEW_SHAPE_STYLE,
    );
    if (this.ellipsePreviewId) {
      this.addPreviewId(this.ellipsePreviewId);
    }
  }

  protected updatePreview(screen: Point2D, world: Point3D): void {
    const overlay = this.viewer.overlayManager;
    const anchorWorld = this.getSnapReferencePoint();
    if (!overlay || !this.ellipsePreviewId || !anchorWorld) return;

    const anchorScreen = this.viewer.worldToScreen(anchorWorld);
    const bounds = getBoundsFromPoints(
      anchorScreen,
      screen,
      anchorWorld,
      world,
    );

    const cx = bounds.screenMin.x + bounds.width / 2;
    const cy = bounds.screenMin.y + bounds.height / 2;
    const radiusx = bounds.width / 2;
    const radiusy = bounds.height / 2;

    overlay.update(this.ellipsePreviewId, {
      cx,
      cy,
      radiusx,
      radiusy,
    });

    this.updateMeasurements(bounds);
  }

  private updateMeasurements(bounds: PointBounds): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    if (!this.measureLabels) {
      this.measureLabels = new MeasurementLabels(
        overlay,
        this.viewer.getBackgroundColor(),
      );
    }
    this.measureLabels.setLine(
      "width",
      bounds.screenMin.x,
      bounds.screenMin.y,
      bounds.screenMax.x,
      bounds.screenMin.y,
    );
    this.measureLabels.setLabel(
      "width",
      bounds.screenMin.x + bounds.width / 2,
      bounds.screenMin.y - 18,
      MeasurementLabels.formatDistance(bounds.worldWidth),
    );
    this.measureLabels.setLine(
      "height",
      bounds.screenMax.x,
      bounds.screenMin.y,
      bounds.screenMax.x,
      bounds.screenMax.y,
    );
    this.measureLabels.setLabel(
      "height",
      bounds.screenMax.x + 18,
      bounds.screenMin.y + bounds.height / 2,
      MeasurementLabels.formatDistance(bounds.worldHeight),
    );
  }

  // ============== Entity Creation ==============

  protected createEntity(a: Point3D, b: Point3D): void {
    const db = this.viewer.database;
    if (!db) return;

    const bounds = getBoundsFromPoints(
      this.viewer.worldToScreen(a),
      this.viewer.worldToScreen(b),
      a,
      b,
    );

    const noteConfig = this.viewer.activeNoteConfig;
    const entityInfo = buildEllipseEntity(
      this.viewer,
      bounds.worldMin,
      bounds.worldMax,
    );

    if (entityInfo) {
      this.applyNoteConfigToEntity(entityInfo.id);

      const entityIds: DrawingModule.OdDbObjectId[] = [entityInfo.id];

      const hatchId = this.createHatchForEntity(entityInfo.id);
      if (hatchId) {
        entityIds.push(hatchId);
      }

      const centerX = (bounds.worldMin.x + bounds.worldMax.x) / 2;
      const centerY = (bounds.worldMin.y + bounds.worldMax.y) / 2;
      const labelId = this.createLabelIfEnabled(centerX, centerY);
      if (labelId) entityIds.push(labelId);

      const command = new AddEntitiesCommand(this.viewer, {
        entityHandles: entityIds.map((id) => getEntityHandle(id)),
        originalLayerName: noteConfig.props.layername ?? "0",
      });
      this.viewer.executeCommand(command);
      trackShape("ellipse");

      const extras = labelId ? [labelId] : [];
      this.selectCreatedEntity(entityInfo.id, extras);
      for (const id of entityIds) {
        deleteObj(id);
      }
    }
  }

  protected override clearLiveMeasurements(): void {
    this.measureLabels?.clear();
    this.measureLabels = null;
  }

  protected clear(): void {
    super.clear();
    this.measureLabels?.clear();
    this.measureLabels = null;
    this.ellipsePreviewId = null;
  }
}

/**
 * Registered dragger for DraggerType.Ellipse. Routes to either the classic
 * single-stroke engine or the two-point sequential engine, toggled via
 * setTwoPointMode() (see DraggerMenuState.sequentialPointMode).
 */
export class EllipseDragger extends DualModeDragger<
  EllipseOneWayEngine,
  EllipseTwoPointEngine
> {
  readonly type = DraggerType.Ellipse;

  constructor(viewer: DRDrawingViewer, config: DraggerConfig = {}) {
    super(
      viewer,
      config,
      new EllipseOneWayEngine(viewer, config),
      new EllipseTwoPointEngine(viewer, config),
    );
  }
}
